import { yupResolver } from "@hookform/resolvers/yup";
import {
  Flex,
  Text,
  Link,
  Modal,
  type CountryCode,
  CheckboxField,
  scrollToInvalidField,
  FormLayout,
} from "@wayflyer/flyui";
import { getDobYearPickerRange, getIdentifierInputProps, RhfProgressiveAddressField, useErrorModal } from "shared-ui";
import { RhfDatePickerField, RhfTextField } from "@wayflyer/flyui-hook-form";
import { useState } from "react";
import { Controller, type DefaultValues, useForm } from "react-hook-form";
import { type IntlShape, useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";
import type { FundingApplicationSchemas } from "api-types";

import {
  useDownloadConsentDocument,
  useCreditScoreConsent,
  useCreditScoreConsentMutation,
  useUpdateCreditScoreConsentMutation,
} from "../../queries/credit-score-consent/queries";

import { MESSAGES } from "./message-definitions";

const formatDateWithZeroPadding = (dateString?: string): string | undefined => {
  if (!dateString) {
    return dateString;
  }

  const dateParts = dateString.split("-");
  if (dateParts.length === 3) {
    const [year, month, day] = dateParts;
    const paddedMonth = month.padStart(2, "0");
    const paddedDay = day.padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
  }

  return dateString;
};

const requiredIfHasSsn = (buildRequiredSchema: () => Yup.StringSchema) => {
  return Yup.string().when("explicitNoSsn", {
    is: false,
    then: () => buildRequiredSchema(),
    otherwise: (schema) => schema.optional(),
  });
};

const makeCreditCheckConsentSchema = (intl: IntlShape) =>
  Yup.object().shape({
    address_line_1: requiredIfHasSsn(() => Yup.string().required(intl.formatMessage(MESSAGES.addressError))),
    address_line_2: Yup.string().optional(),
    city: requiredIfHasSsn(() => Yup.string().required(intl.formatMessage(MESSAGES.addressError))),
    state: requiredIfHasSsn(() => Yup.string().required(intl.formatMessage(MESSAGES.addressError))),
    zip_code: requiredIfHasSsn(() => Yup.string().required(intl.formatMessage(MESSAGES.addressError))),
    country_code: requiredIfHasSsn(() => Yup.string().required(intl.formatMessage(MESSAGES.addressError))),
    firstName: requiredIfHasSsn(() => Yup.string().trim().required(intl.formatMessage(MESSAGES.firstNameError))),
    lastName: requiredIfHasSsn(() => Yup.string().trim().required(intl.formatMessage(MESSAGES.lastNameError))),
    dateOfBirth: requiredIfHasSsn(() =>
      Yup.string()
        .trim()
        .required(intl.formatMessage(MESSAGES.dateOfBirthError))
        .test("is-valid-date", intl.formatMessage(MESSAGES.dateOfBirthInvalidError), (value) => {
          if (!value) {
            return false;
          }
          const dateRegex = /^\d{4}-(0?[1-9]|1[0-2])-(0?[1-9]|[12]\d|3[01])$/;
          return dateRegex.test(value);
        })
    ),
    ssn: requiredIfHasSsn(() =>
      Yup.string()
        .required(intl.formatMessage(MESSAGES.ssnError))
        .test("ssn-format", intl.formatMessage(MESSAGES.ssnFormatError), (value) => {
          if (!value) {
            return false;
          }
          const digitsOnly = value.replace(/-/g, "");
          return /^\d{9}$/.test(digitsOnly);
        })
    ),
    explicitNoSsn: Yup.bool(),
    maskedSsn: Yup.string().nullable().optional(),
  });

type CreditCheckConsentSchema = ReturnType<typeof makeCreditCheckConsentSchema>;
type CreditCheckConsentFormData = Yup.InferType<CreditCheckConsentSchema>;
type CreditCheckConsentFormInput = Yup.InferType<CreditCheckConsentSchema>;

type CreditCheckConsentEntryFormProps = {
  formId: string;
  initialValues: DefaultValues<CreditCheckConsentFormInput>;
  onSubmit: (formData: CreditCheckConsentFormData) => void;
  onDownloadConsentDocument?: () => void;
};

const CreditCheckConsentEntryForm = ({
  formId,
  initialValues,
  onSubmit,
  onDownloadConsentDocument,
}: CreditCheckConsentEntryFormProps) => {
  const intl = useIntl();
  const [showNoSsnConfirmationModal, setShowNoSsnConfirmationModal] = useState(false);

  const schema = makeCreditCheckConsentSchema(intl);

  const form = useForm<CreditCheckConsentFormInput, unknown, CreditCheckConsentFormData>({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const explicitNoSsn = form.watch("explicitNoSsn");
  const countryCode = form.watch("country_code") as CountryCode;

  const ADDRESS_FIELD_NAMES = ["address_line_1", "city", "state", "zip_code", "country_code"] as const;
  const addressErrorMessage = ADDRESS_FIELD_NAMES.map((name) => form.formState.errors[name]?.message).find(Boolean);

  const onConsentSubmit = (formData: CreditCheckConsentFormData) => {
    const formattedFormData = {
      ...formData,
      dateOfBirth: formatDateWithZeroPadding(formData.dateOfBirth),
    };
    onSubmit(formattedFormData);
  };

  const onSubmitHandler = (formData: CreditCheckConsentFormData) => {
    if (formData.explicitNoSsn) {
      setShowNoSsnConfirmationModal(true);
    } else {
      onConsentSubmit(formData);
    }
  };

  const handleAddSsn = () => {
    setShowNoSsnConfirmationModal(false);
    form.setValue("explicitNoSsn", false);
  };

  const handleContinueWithoutSsn = () => {
    setShowNoSsnConfirmationModal(false);
    onConsentSubmit(form.getValues());
  };

  return (
    <>
      <form id={formId} onSubmit={form.handleSubmit(onSubmitHandler, scrollToInvalidField)}>
        <Flex direction="column" maxWidth="550px">
          <FormLayout>
            <Flex direction="column" gap="3">
              <RhfTextField
                form={form}
                name="ssn"
                label={intl.formatMessage(MESSAGES.ssnLabel)}
                disabled={explicitNoSsn}
                defaultValue={initialValues.ssn}
                {...getIdentifierInputProps(intl, {
                  maskPreset: "US_SSN",
                  helperText:
                    initialValues.maskedSsn && !form.formState.dirtyFields.ssn && !explicitNoSsn
                      ? { text: intl.formatMessage(MESSAGES.ssnHelperText), showInfoIcon: true }
                      : undefined,
                })}
              />
              <Controller
                name="explicitNoSsn"
                control={form.control}
                render={({ field }) => (
                  <CheckboxField
                    name="explicitNoSsn"
                    options={{
                      name: "explicitNoSsn",
                      label: intl.formatMessage(MESSAGES.noSsnLabel),
                      defaultChecked: initialValues.explicitNoSsn,
                      checked: field.value,
                      onChange: (checked: boolean) => {
                        if (checked) {
                          form.resetField("ssn");
                          form.clearErrors();
                        }
                        field.onChange(checked);
                      },
                    }}
                  />
                )}
              />
            </Flex>

            <Flex direction="row" gap="6">
              <RhfTextField
                form={form}
                name="firstName"
                disabled={explicitNoSsn}
                label={intl.formatMessage(MESSAGES.firstNameLabel)}
              />
              <RhfTextField
                form={form}
                name="lastName"
                disabled={explicitNoSsn}
                label={intl.formatMessage(MESSAGES.lastNameLabel)}
              />
            </Flex>

            <RhfDatePickerField
              form={form}
              name="dateOfBirth"
              disabled={explicitNoSsn}
              label={intl.formatMessage(MESSAGES.dateOfBirthLabel)}
              yearPicker={getDobYearPickerRange()}
            />

            <RhfProgressiveAddressField
              form={form}
              fieldMapping={{
                addressLine1: "address_line_1",
                addressLine2: "address_line_2",
                city: "city",
                state: "state",
                postalCode: "zip_code",
                countryCode: "country_code",
              }}
              label={intl.formatMessage(MESSAGES.addressLabel)}
              countryCode={countryCode}
              disabled={explicitNoSsn}
              errorMessage={addressErrorMessage}
            />

            <Text color="placeholder" size="xs">
              {intl.formatMessage(MESSAGES.agreeAndContinueText, {
                link: (linkText: React.ReactNode) => <Link onClick={onDownloadConsentDocument}>{linkText}</Link>,
              })}
            </Text>
          </FormLayout>
        </Flex>
      </form>

      <Modal
        isOpen={showNoSsnConfirmationModal}
        onClose={handleAddSsn}
        title={intl.formatMessage(MESSAGES.noSsnModalTitle)}
        primaryAction={{
          content: intl.formatMessage(MESSAGES.submitWithoutSsnButton),
          onAction: handleContinueWithoutSsn,
        }}
        tertiaryAction={{
          content: intl.formatMessage(MESSAGES.addSsnButton),
          onAction: handleAddSsn,
        }}
      >
        <Flex direction="column" gap="4">
          <Text>{intl.formatMessage(MESSAGES.noSsnModalContent)}</Text>
        </Flex>
      </Modal>
    </>
  );
};

export interface CreditCheckConsentFormConnectorProps {
  formId: string;
  onContinue: () => void;
}

export const CreditCheckConsent = ({ formId, onContinue }: CreditCheckConsentFormConnectorProps) => {
  const { openErrorModal } = useErrorModal();
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const { data: existingConsent } = useCreditScoreConsent(customerDomain);
  const createConsentMutation = useCreditScoreConsentMutation(customerDomain);
  const updateConsentMutation = useUpdateCreditScoreConsentMutation(customerDomain);
  const { refetch: fetchConsentDocument } = useDownloadConsentDocument(customerDomain);

  const downloadConsentDocument = async () => {
    try {
      const response = await fetchConsentDocument();
      if (response.data) {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "Wayflyer Credit Check Consent.pdf";
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      openErrorModal({ variant: "submit-error", error: error as Error });
    }
  };

  const onConsentSubmit = async (formData: CreditCheckConsentFormData) => {
    try {
      let requestData: FundingApplicationSchemas.CreditScoreConsentRequestSchema = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData.dateOfBirth,
        ssn: formData.ssn,
        explicitNoSsn: false,
        address: {
          address1: formData.address_line_1 ?? "",
          address2: formData.address_line_2,
          city: formData.city,
          postcode: formData.zip_code,
          state: formData.state,
          country: formData.country_code,
        },
        consentGranted: true,
      };
      if (formData.explicitNoSsn) {
        requestData = {
          explicitNoSsn: true,
          consentGranted: false,
        };
      }

      if (existingConsent) {
        await updateConsentMutation.mutateAsync(requestData);
      } else {
        await createConsentMutation.mutateAsync(requestData);
      }
      onContinue();
    } catch (error) {
      openErrorModal({ variant: "submit-error", error: error as Error });
    }
  };

  return (
    <CreditCheckConsentEntryForm
      formId={formId}
      onSubmit={onConsentSubmit}
      onDownloadConsentDocument={downloadConsentDocument}
      initialValues={{
        firstName: existingConsent?.firstName || "",
        lastName: existingConsent?.lastName || "",
        dateOfBirth: existingConsent?.dateOfBirth || undefined,
        ssn: "",
        maskedSsn: existingConsent?.maskedSsn,
        explicitNoSsn: existingConsent?.explicitNoSsn ?? false,
        address_line_1: existingConsent?.address?.address1 || "",
        address_line_2: existingConsent?.address?.address2 || "",
        city: existingConsent?.address?.city || "",
        state: existingConsent?.address?.state || "",
        zip_code: existingConsent?.address?.postcode || "",
        country_code: (existingConsent?.address?.country || countryOfIncorporation) as string,
      }}
    />
  );
};
